/**
 * 手動登錄付款(REQ-20260815002)。
 *
 * 應付沖帳畫面原本只看得到「還欠廠商多少」,錢付出去了沒有地方登錄 ——
 * 沖帳紀錄只有 mdb 匯入那一條路徑。結構與 `ReceiptModal.tsx` 逐段對稱:
 * 勾未結進貨單 → 每筆預帶入未結金額(可改成部分付款)→ 填付款日期與付款方式 → 送出。
 *
 * 金額只是「要沖多少」的意圖,後端會拿當下的未結餘額重算與驗證;
 * 這裡的檢查純粹是為了不要讓使用者按下去才發現超付。
 */
import { useState } from 'react';
import type { CreateApSettlementInput, PurchaseOrderListItem } from '@app/shared';
import { Modal } from '../../components/Modal';
import { formatMoney } from '../../lib/format-money';
import { useCreateApSettlement } from '../accounting/api';

export interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  supplierId: number;
  /** 未結應付明細,由 `useApOutstanding()` 帶進來,不在這裡重查 */
  outstanding: PurchaseOrderListItem[];
}

export function PaymentModal({
  open,
  onClose,
  supplierId,
  outstanding,
}: PaymentModalProps): JSX.Element {
  const mutation = useCreateApSettlement(supplierId);
  /** 勾選的單號 → 這次要沖的金額(字串,維持 decimal 精度) */
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [settledAt, setSettledAt] = useState(todayInTaipei);
  const [cashAmount, setCashAmount] = useState('');
  const [checkAmount, setCheckAmount] = useState('');
  const [usePrepaid, setUsePrepaid] = useState('');

  const rows = outstanding.map((o) => ({ order: o, amount: picked[o.orderNo] }));
  const selected = rows.filter((r) => r.amount !== undefined);
  const total = sumAmounts(selected.map((r) => r.amount ?? '0'));
  // 超付的單直接標出來 —— 後端也會擋,但先擋在這裡才不用等一次來回
  const overpaid = selected.filter((r) => Number(r.amount) > Number(r.order.outstanding));
  const invalid = selected.filter((r) => !(Number(r.amount) > 0));

  const toggle = (order: PurchaseOrderListItem): void => {
    setPicked((prev) => {
      const next = { ...prev };
      // 勾起來時預帶入該單的未結金額,要部分付款再自己改
      if (next[order.orderNo] === undefined) next[order.orderNo] = order.outstanding;
      else delete next[order.orderNo];
      return next;
    });
  };

  const submit = (): void => {
    const input: CreateApSettlementInput = {
      settledAt,
      cashAmount: cashAmount.trim() || null,
      checkAmount: checkAmount.trim() || null,
      usePrepaid: usePrepaid.trim() || null,
      addPrepaid: null,
      lines: selected.map((r) => ({
        sourceDocNo: r.order.orderNo,
        settledAmount: r.amount ?? '0',
      })),
    };
    mutation.mutate(input, {
      onSuccess: () => {
        setPicked({});
        setCashAmount('');
        setCheckAmount('');
        setUsePrepaid('');
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="登錄付款"
      dismissOnBackdrop={false}
      widthClass="max-w-3xl"
    >
      <div className="grid gap-4">
        {outstanding.length === 0 ? (
          <div className="text-slate-500 text-sm">
            這個廠商目前沒有未結的應付款項,不需要登錄付款。
          </div>
        ) : (
          <>
            <table className="w-full bg-white shadow-sm rounded-md">
              <thead>
                <tr className="border-b text-left text-sm text-slate-600">
                  <th className="px-4 py-2 w-10"> </th>
                  <th className="px-4 py-2">日期</th>
                  <th className="px-4 py-2">單號</th>
                  <th className="px-4 py-2 text-right">未結</th>
                  <th className="px-4 py-2 text-right">本次沖銷</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ order, amount }) => (
                  <tr key={order.id} className="border-b last:border-0 hover:bg-slate-50 text-sm">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        aria-label={`選擇 ${order.orderNo}`}
                        data-testid={`payment-pick-${order.orderNo}`}
                        checked={amount !== undefined}
                        onChange={() => toggle(order)}
                      />
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{order.txnDate}</td>
                    <td className="px-4 py-2 whitespace-nowrap font-mono">{order.orderNo}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {formatMoney(order.outstanding)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input
                        type="text"
                        inputMode="decimal"
                        aria-label={`${order.orderNo} 沖銷金額`}
                        data-testid={`payment-amount-${order.orderNo}`}
                        disabled={amount === undefined}
                        value={amount ?? ''}
                        onChange={(e) =>
                          setPicked((prev) => ({ ...prev, [order.orderNo]: e.target.value }))
                        }
                        className="border rounded-md px-2 py-1 text-sm w-28 text-right font-mono disabled:bg-slate-100"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="grid gap-1">
                <label className="text-sm text-slate-600" htmlFor="payment-settled-at">
                  付款日期
                </label>
                <input
                  id="payment-settled-at"
                  type="date"
                  data-testid="payment-settled-at"
                  value={settledAt}
                  onChange={(e) => setSettledAt(e.target.value)}
                  className="border rounded-md px-3 py-2 text-sm"
                />
              </div>
              <MoneyField
                id="payment-cash"
                label="現金"
                value={cashAmount}
                onChange={setCashAmount}
              />
              <MoneyField
                id="payment-check"
                label="支票"
                value={checkAmount}
                onChange={setCheckAmount}
              />
              <MoneyField
                id="payment-use-prepaid"
                label="動用預付款"
                value={usePrepaid}
                onChange={setUsePrepaid}
              />
            </div>

            <p className="text-xs text-slate-500">
              付款方式金額是「錢怎麼付出去的」註記,沖帳合計一律以上方勾選的明細加總為準。
              談好不付的尾款請走進貨單的「折讓」,不要在這裡少沖。
            </p>

            {overpaid.length > 0 ? (
              <div role="alert" className="text-red-600 text-sm">
                {overpaid.map((r) => r.order.orderNo).join('、')} 的沖銷金額超過未結餘額。
              </div>
            ) : null}
            {mutation.isError ? (
              <div role="alert" className="text-red-600 text-sm">
                登錄失敗：
                {mutation.error instanceof Error ? mutation.error.message : '未知錯誤'}
              </div>
            ) : null}

            <div className="flex items-center justify-between border-t pt-3">
              <div className="text-sm text-slate-600">
                已選 {selected.length} 筆,沖帳合計{' '}
                <span data-testid="payment-total" className="font-mono text-slate-900">
                  {total}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded border hover:bg-slate-50"
                >
                  取消
                </button>
                <button
                  type="button"
                  data-testid="payment-submit"
                  onClick={submit}
                  disabled={
                    selected.length === 0 ||
                    overpaid.length > 0 ||
                    invalid.length > 0 ||
                    mutation.isPending
                  }
                  className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {mutation.isPending ? '登錄中...' : '確認付款'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function MoneyField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <label className="text-sm text-slate-600" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        data-testid={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-3 py-2 text-sm text-right font-mono"
      />
    </div>
  );
}

/** 顯示用的合計。權威金額由後端以 Decimal 重算,這裡只是給人看的即時回饋。 */
function sumAmounts(amounts: string[]): string {
  const cents = amounts.reduce((sum, a) => sum + Math.round((Number(a) || 0) * 100), 0);
  return (cents / 100).toFixed(2);
}

/** 付款日期預設今天(台北)—— 容器與瀏覽器時區都不一定是台北,明確指定 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
